const cents = (value) => Math.round((Number(value) || 0) * 100);
const normalize = (value) => String(value || "").trim().toLowerCase();
export const containerAdvance = (row) => cents(row?.advancePayment ?? row?.AdvancePayment) / 100;
export const blVesselKey = (row) => {
  const bl = normalize(row?.blNo ?? row?.BLNo);
  const vessel = normalize(row?.vesselName ?? row?.VesselName);
  return bl && vessel ? JSON.stringify([bl, vessel]) : null;
};

export const buildBLInvoices = (rows) => {
  const groups = new Map();
  rows.forEach((row, index) => {
    const key = blVesselKey(row) || `container:${index}`;
    if (!groups.has(key)) groups.set(key, {
      key, blNo: row.blNo ?? row.BLNo ?? "", vesselName: row.vesselName ?? row.VesselName ?? "",
      invoiceNo: row.invoiceNo ?? row.InvoiceNo ?? "", invoiceDate: row.invoiceDate ?? row.InvoiceDate ?? "",
      rows: [], indexes: [], grossCents: 0, advanceCents: 0
    });
    const group = groups.get(key);
    group.rows.push(row);
    group.indexes.push(index);
    group.grossCents += cents(row.invoiceAmount ?? row.InvoiceAmount);
    // Legacy rows may contain an old per-container share. Never add these
    // together; a subsequent explicit edit sets the whole group's amount.
    group.advanceCents = Math.max(group.advanceCents, cents(containerAdvance(row)));
  });
  return [...groups.values()].map(group => ({ ...group,
    invoiceAmount: group.grossCents / 100,
    advancePayment: group.advanceCents / 100,
    balanceAmount: (group.grossCents - group.advanceCents) / 100
  }));
};

export const applyBLAdvance = (rows, source) => {
  const key = blVesselKey(source);
  if (!key) return rows;
  return rows.map(row => blVesselKey(row) === key
    ? { ...row, advancePayment: containerAdvance(source), invoiceNo: source.invoiceNo ?? row.invoiceNo, invoiceDate: source.invoiceDate ?? row.invoiceDate }
    : row);
};

export const getShipmentTotals = (rows) => {
  const groups = buildBLInvoices(rows);
  const invoice = groups.reduce((sum, group) => sum + cents(group.invoiceAmount), 0);
  const advance = groups.reduce((sum, group) => sum + cents(group.advancePayment), 0);
  return { invoiceAmount: invoice / 100, advancePayment: advance / 100, balanceAmount: (invoice - advance) / 100 };
};

export const normalizeBLAdvances = (rows) => {
  const next = rows.map(row => ({ ...row }));
  for (const group of buildBLInvoices(rows)) {
    // Keep the full BL advance visible on each container; apply the deduction
    // just once to the stored balances, without dividing it between containers.
    const first = [...group.indexes].sort((a, b) =>
      (Number(rows[a].ShipmentID) || Number.MAX_SAFE_INTEGER) - (Number(rows[b].ShipmentID) || Number.MAX_SAFE_INTEGER))[0];
    for (const index of group.indexes) {
      next[index].advancePayment = group.advancePayment;
      next[index].balanceAmount = (cents(rows[index].invoiceAmount ?? rows[index].InvoiceAmount) - (index === first ? group.advanceCents : 0)) / 100;
    }
  }
  return next;
};

export const previewBLRows = (rows, editingIndex, draft) => {
  const current = typeof draft === "object" ? draft : { ...(rows[editingIndex] || {}), advancePayment: draft };
  const next = rows.map((row, index) => index === editingIndex ? { ...row, ...current } : row);
  if (editingIndex === null || editingIndex === undefined) next.push(current);
  return applyBLAdvance(next, current);
};

export const getAdvanceAllocation = (paid, rows, editingIndex, draft) => {
  const allocatedCents = buildBLInvoices(previewBLRows(rows, editingIndex, draft))
    .reduce((sum, group) => sum + group.advanceCents, 0);
  return { total: cents(paid) / 100, allocated: allocatedCents / 100, pending: (cents(paid) - allocatedCents) / 100 };
};
